import { useRef, useEffect, useState } from 'react';
import { debounce } from 'radash';
import { colors } from '../../constants';

export const TimesChart = ({ times, labels }) => {
  const parser = useRef(new Parser());

  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const wrapperRef = useRef(null);

  const [tick, setTick] = useState(0);
  const [layout, setLayout] = useState([[]]);
  const [assigned, setAssigned] = useState({});
  const [height, setHeight] = useState(100);
  const [width, setWidth] = useState(window.innerWidth);

  const topGap = 12;
  const barHeight = labels ? 16 : 2;
  const gap = 0.5;
  const rowHeight = barHeight + gap;

  useEffect(() => {
    const handle = debounce({ delay: 200 }, () => {
      setWidth(wrapperRef?.current?.offsetWidth);
    });
    window.addEventListener('resize', handle);
    handle();
    return () => window.removeEventListener('resize', handle);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 20);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const layout_ = parser.current.parseTimes(times);
    setLayout(layout_);
    setHeight(2 + topGap + layout_.length * rowHeight);
  }, [tick, times, rowHeight]);

  useEffect(() => {
    const now = new Date().getTime();

    const startedTimes = times.map((it) => it.started);
    if (!startedTimes.length) {
      startedTimes.push(now);
    }
    const start = Math.min(...startedTimes);
    const finishedTimes = times.map((it) => it.finished).filter(Boolean);
    if (!finishedTimes.length) {
      finishedTimes.push(now + 1);
    }
    const end = Math.max(...finishedTimes);

    const widths = times.map((it) => {
      const started = it.started || now;
      const finished = it.finished || now;
      const offset = started - start;
      return offset + finished - started;
    });

    const duration = widths.length ? Math.max(...widths) : end - start;
    const levels = [
      [1_000, 500, 500],
      [2_000, 1_000, 500],
      [5_000, 5_000, 1_000],
      [30_000, 10_000, 5_000],
      [60_000, 15_000, 10_000],
      [300_000, 60_000, 60_000],
    ];
    let roundTo = 60_000;
    let xInterval = 10_000;
    for (const [cutoff, roundTo_, xInterval_] of levels) {
      if (duration < cutoff) {
        roundTo = roundTo_;
        xInterval = xInterval_;
        break;
      }
    }

    const totalDuration = roundTo * Math.ceil(duration / roundTo);
    // console.log('totalDuration', duration, totalDuration, roundTo);
    // console.log('widths', duration, Math.max(...widths), widths);

    const dpr = window.devicePixelRatio || 1;
    const getCtx = (c) => {
      c.style.width = width + 'px';
      c.style.height = height + 'px';
      c.width = width * dpr;
      c.height = height * dpr;
      const x = c.getContext('2d');
      x.scale(dpr, dpr);
      return x;
    };

    const offscreen = offscreenRef.current;
    const offCtx = getCtx(offscreen);

    if (!offscreen.width || !offscreen.height) {
      return;
    }

    offCtx.clearRect(0, 0, width, height);

    const drawRect = (x, y, w, h, color) => {
      offCtx.fillStyle = 'white';
      offCtx.fillRect(x, y, 1, h);
      offCtx.fillStyle = color;
      offCtx.fillRect(x, y, w, h);
    };

    const fillTextWithClip = (text, x, y, maxWidth, height) => {
      offCtx.save();
      offCtx.beginPath();
      offCtx.rect(x, y - height, maxWidth, 2 * height);
      offCtx.clip();
      offCtx.fillText(text, x + 1, y);
      offCtx.restore();
    };

    for (let i = 0; i < layout.length; i++) {
      const row = layout[i];
      for (const item of row) {
        const offset = item.started - start;
        const duration = item.finished - item.started;

        const x = Math.round(width * (offset / totalDuration)) + 1;
        const y = (rowHeight + gap) * i;
        const w = Math.round(width * (duration / totalDuration)) - 2;
        const h = barHeight;

        // const spaceUsed = x + w;
        // if (spaceUsed > width) {
        //   console.log(`space used: duration=${duration} offset=${offset} x=${x} w=${w} spaceUsed=${spaceUsed} width=${width} totalDuration=${totalDuration}`);
        // }

        const color =
          {
            completed: colors.green,
            failed: colors.lightError,
          }[item.state] || '#aaa';

        drawRect(x, topGap + y, w, h, color);

        if (labels) {
          offCtx.fillStyle = 'white';
          fillTextWithClip(
            `${item.label} (${(duration / 1000).toFixed(1)}s)`,
            x + 2,
            y + 6 + rowHeight,
            w - 4,
            rowHeight,
          );
        }
      }
    }

    offCtx.font = '10px Arial';
    offCtx.fillStyle = '#888';

    for (let i = 0; i < totalDuration; i += xInterval) {
      const x = width * (i / totalDuration);

      const pretty = (seconds) => {
        return `${Math.floor(seconds / 60)}m${seconds % 60}s`;
      };

      offCtx.fillText(`${pretty(i / 1000)}`, x, 8);
    }

    const canvas = canvasRef.current;
    const ctx = getCtx(canvas);
    ctx.clearRect(0, 0, width, height);
    ctx.scale(1 / dpr, 1 / dpr);
    ctx.drawImage(offscreen, 0, 0);
  }, [tick]);

  const hasChildren = times?.length > 0;

  return (
    <div ref={wrapperRef} style={{ width: '100%', border: '1px solid #ccc', padding: 5, borderRadius: 5 }}>
      <canvas style={{ display: hasChildren ? null : 'none' }} ref={canvasRef} width={width} height={height} />

      <canvas
        style={{ display: 'none', border: '1px solid #ccc', padding: '2px 2px 0 2px', borderRadius: 2 }}
        ref={offscreenRef}
        width={width}
        height={height}
      />

      {/*
      tick:{tick}
      <pre>{JSON.stringify(layout, null, 2)}</pre>
          <pre>{JSON.stringify(assigned, null, 2)}</pre>
          <pre>{JSON.stringify(jobs, null, 2)}</pre>
          <pre>{jobs.length}</pre>
          <pre>{jobs.filter(it => !!it.finished).length}</pre>
        */}
    </div>
  );
};

const overlaps = (a, b) => a.started < b.finished && a.finished > b.started;

const Parser = class {
  constructor() {
    this.assigned = {};
  }

  parseTimes(times) {
    const now = new Date().getTime();
    const layout = [[]];
    times.sort((a, b) => a.started - b.started);
    for (const time of times) {
      const state = time.state;
      const label = time.label || '';
      const started = time.started;
      const finished = time.finished || now;

      let rowNum;

      if (false && this.assigned[time.id]) {
        rowNum = this.assigned[time.id];
      } else {
        for (const [idx, row] of layout.entries()) {
          let ok = true;
          for (const item of row) {
            if (overlaps(item, { started, finished, state })) {
              ok = false;
            }
          }
          if (ok) {
            rowNum = idx;
            break;
          }
        }

        if (rowNum === undefined) {
          rowNum = layout.length;
        }
      }

      while (rowNum >= layout.length) {
        layout.push([]);
      }

      layout[rowNum].push({ started, finished, state, label });
    }

    return layout;
  }
};
